import { useEffect, useState, useRef } from 'react';
import client from '../api/client';
import Layout from '../components/Layout';
import './Queue.css';

const STATUS_ORDER = ['queued', 'in_progress', 'on_hold', 'complete'];

const STATUS_LABELS = {
  queued:      'Queued',
  in_progress: 'In Progress',
  on_hold:     'On Hold',
  complete:    'Complete',
};

const STATUS_COLORS = {
  queued:      { bg: 'rgba(107,114,128,0.12)', color: '#9ca3af' },
  in_progress: { bg: 'rgba(79,142,247,0.12)',  color: '#4f8ef7' },
  on_hold:     { bg: 'rgba(245,158,11,0.12)',  color: '#f59e0b' },
  complete:    { bg: 'rgba(34,197,94,0.12)',   color: '#22c55e' },
};

export default function Queue() {
  const [jobs, setJobs]           = useState([]);
  const [locations, setLocations] = useState([]);
  const [departments, setDepts]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editJob, setEditJob]     = useState(null);
  const [filter, setFilter]       = useState('all');
  const [deptFilter, setDeptFilter] = useState('all');
  const [search, setSearch]       = useState('');
  const [error, setError]         = useState('');
  const [saving, setSaving]       = useState(false);

  const emptyForm = {
    job_number: '', description: '', location_id: '',
    department_id: '', status: 'queued', quantity: '', due_date: '',
  };
  const [form, setForm] = useState(emptyForm);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [jobsRes, locRes, deptRes] = await Promise.all([
        client.get('/jobs'),
        client.get('/locations'),
        client.get('/departments'),
      ]);
      setJobs(jobsRes.data);
      setLocations(locRes.data);
      setDepts(deptRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const openNew = () => {
    setEditJob(null);
    setForm(emptyForm);
    setError('');
    setShowModal(true);
  };

  const openEdit = (job) => {
    setEditJob(job);
    setForm({
      job_number:    job.job_number,
      description:   job.description || '',
      location_id:   job.location_id || '',
      department_id: job.department_id || '',
      status:        job.status,
      quantity:      job.quantity || '',
      due_date:      job.due_date ? job.due_date.split('T')[0] : '',
    });
    setError('');
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      const payload = {
        ...form,
        quantity:      form.quantity ? parseInt(form.quantity) : null,
        location_id:   form.location_id   || null,
        department_id: form.department_id || null,
        due_date:      form.due_date      || null,
      };
      if (editJob) {
        await client.put(`/jobs/${editJob.id}`, payload);
      } else {
        await client.post('/jobs', payload);
      }
      setShowModal(false);
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save job');
    } finally {
      setSaving(false);
    }
  };

  const cycleStatus = async (job) => {
    const next = STATUS_ORDER[(STATUS_ORDER.indexOf(job.status) + 1) % STATUS_ORDER.length];
    try {
      await client.put(`/jobs/${job.id}`, { status: next });
      setJobs(prev => prev.map(j => j.id === job.id ? { ...j, status: next } : j));
    } catch (err) { console.error(err); }
  };

  const deleteJob = async (id) => {
    if (!window.confirm('Delete this job?')) return;
    try {
      await client.delete(`/jobs/${id}`);
      setJobs(prev => prev.filter(j => j.id !== id));
    } catch (err) { console.error(err); }
  };

  const filtered = jobs.filter(j => {
    if (filter !== 'all' && j.status !== filter) return false;
    if (deptFilter !== 'all' && j.department_id !== deptFilter) return false;
    if (search && !j.job_number.toLowerCase().includes(search.toLowerCase()) &&
        !(j.description || '').toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const counts = STATUS_ORDER.reduce((acc, s) => {
    acc[s] = jobs.filter(j => j.status === s).length;
    return acc;
  }, {});

  if (loading) return <Layout><div className="q-loading">Loading queue...</div></Layout>;

  return (
    <Layout>
      <div className="q-header">
        <div className="q-header-left">
          <h1 className="q-title">Queue</h1>
          <div className="q-count">{filtered.length} job{filtered.length !== 1 ? 's' : ''}</div>
        </div>
        <button className="q-btn-new" onClick={openNew}>+ New Job</button>
      </div>

      {/* Quick status filters */}
      <div className="q-filters">
        <button
          className={`q-filter-btn ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
        >
          All <span className="q-filter-count">{jobs.length}</span>
        </button>
        {STATUS_ORDER.map(s => (
          <button
            key={s}
            className={`q-filter-btn ${filter === s ? 'active' : ''}`}
            style={filter === s ? { color: STATUS_COLORS[s].color, borderColor: STATUS_COLORS[s].color } : {}}
            onClick={() => setFilter(s)}
          >
            {STATUS_LABELS[s]} <span className="q-filter-count">{counts[s]}</span>
          </button>
        ))}
      </div>

      {/* Search + dept filter */}
      <div className="q-search-row">
        <input
          className="q-search"
          placeholder="Search job # or description..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {departments.length > 0 && (
          <select className="q-dept-select" value={deptFilter} onChange={e => setDeptFilter(e.target.value)}>
            <option value="all">All Departments</option>
            {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        )}
      </div>

      {/* Job cards */}
      <div className="q-list">
        {filtered.length === 0 && (
          <div className="q-empty">No jobs match your filters.</div>
        )}
        {filtered.map(job => {
          const sc = STATUS_COLORS[job.status];
          const dept = departments.find(d => d.id === job.department_id);
          const loc  = locations.find(l => l.id === job.location_id);
          return (
            <div key={job.id} className="q-card" onClick={() => openEdit(job)}>
              <div className="q-card-left">
                <div className="q-card-num">{job.job_number}</div>
                {job.description && <div className="q-card-desc">{job.description}</div>}
                <div className="q-card-meta">
                  {loc  && <span className="q-meta-tag">{loc.name}</span>}
                  {dept && <span className="q-meta-tag" style={{ color: dept.color }}>● {dept.name}</span>}
                  {job.quantity && <span className="q-meta-tag q-meta-qty">Qty: {job.quantity}</span>}
                  {job.due_date && (() => {
                    const due  = new Date(job.due_date);
                    const now  = new Date();
                    const diff = (due - now) / (1000 * 60 * 60 * 24);
                    const isPast    = diff < 0;
                    const isWarning = diff >= 0 && diff <= 2;
                    return (
                      <span
                        className={`q-meta-tag q-meta-due ${isPast ? 'past-due' : isWarning ? 'due-soon' : ''}`}
                      >
                        {isPast ? 'Past Due' : `Due: ${due.toLocaleDateString()}`}
                      </span>
                    );
                  })()}
                </div>
              </div>
              <div className="q-card-right">
                <button
                  className="q-status-btn"
                  style={{ background: sc.bg, color: sc.color }}
                  onClick={e => { e.stopPropagation(); cycleStatus(job); }}
                  title="Click to advance status"
                >
                  {STATUS_LABELS[job.status]}
                </button>
                <button
                  className="q-delete-btn"
                  onClick={e => { e.stopPropagation(); deleteJob(job.id); }}
                >✕</button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="q-overlay" onClick={() => setShowModal(false)}>
          <div className="q-modal" onClick={e => e.stopPropagation()}>
            <div className="q-modal-header">
              <h2>{editJob ? `Edit ${editJob.job_number}` : 'New Job'}</h2>
              <button className="q-modal-close" onClick={() => setShowModal(false)}>✕</button>
            </div>
            {error && <div className="q-modal-error">{error}</div>}
            <form onSubmit={handleSubmit} className="q-modal-form">
              <div className="q-field">
                <label>Job Number *</label>
                <input
                  value={form.job_number}
                  onChange={e => setForm({...form, job_number: e.target.value})}
                  placeholder="e.g. 78011"
                  required
                  disabled={!!editJob}
                />
              </div>
              <div className="q-field">
                <label>Description</label>
                <input
                  value={form.description}
                  onChange={e => setForm({...form, description: e.target.value})}
                  placeholder="e.g. 42 Plain QDP Print"
                />
              </div>
              <div className="q-row">
                <div className="q-field">
                  <label>Status</label>
                  <select value={form.status} onChange={e => setForm({...form, status: e.target.value})}>
                    {STATUS_ORDER.map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
                  </select>
                </div>
                <div className="q-field">
                  <label>Quantity</label>
                  <input
                    type="number"
                    value={form.quantity}
                    onChange={e => setForm({...form, quantity: e.target.value})}
                    placeholder="0"
                  />
                </div>
              </div>
              <div className="q-row">
                <div className="q-field">
                  <label>Location</label>
                  <select value={form.location_id} onChange={e => setForm({...form, location_id: e.target.value})}>
                    <option value="">No Location</option>
                    {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                  </select>
                </div>
                <div className="q-field">
                  <label>Department</label>
                  <select value={form.department_id} onChange={e => setForm({...form, department_id: e.target.value})}>
                    <option value="">No Department</option>
                    {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                </div>
              </div>
              <div className="q-field">
                <label>Due Date</label>
                <input
                  type="date"
                  value={form.due_date}
                  onChange={e => setForm({...form, due_date: e.target.value})}
                />
              </div>
              <div className="q-modal-actions">
                <button type="button" className="q-btn-cancel" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="q-btn-save" disabled={saving}>
                  {saving ? 'Saving...' : editJob ? 'Save Changes' : 'Create Job'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}